import { 
  ArrowUpTrayIcon,
  TrashIcon,
  CheckCircleIcon,
  CheckBadgeIcon,
  ClockIcon,
  SparklesIcon,
  WrenchScrewdriverIcon,
  DocumentTextIcon,
  DocumentArrowDownIcon,
  UserGroupIcon,
  ShareIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CONTEXT_MANAGEMENT_LABEL } from '../../constants/stages';
import { useAuth } from '../../contexts/AuthContext';
import { ProjectItem, ProjectStatus, ProjectType } from '../../types/project';
import {
  ContinueButton,
  MAIN_ACTION_BUTTON_CLASS_NAMES,
} from '../ContinueButton';
import { InviteCollaboratorsModal } from '../InviteCollaboratorsModal';

const GREENFIELD_STAGES = [1, 2, 3, 4, 5, 6, 7];
const BROWNFIELD_STAGES = [1, 2, 2.5, 3, 4, 5, 6, 7];
const GENERATION_STAGE_NUMBER = 5;
const FINAL_STAGE_NUMBER = 7;
const NOT_APPROVED_BADGE_TEXT = 'v1';
const NOT_YET_APPROVED_TITLE =
  'Not yet approved by all approvers';
const PUBLISH_BUTTON_TITLE = 'Publish project';
const PUBLISH_DISABLED_TITLE =
  'Project must be approved by all approvers before publishing';
const REVIEW_BUTTON_TITLE = 'Go to review';
const REVIEW_DISABLED_TITLE =
  'Project has not been submitted for review yet';
const UPDATE_BUTTON_TITLE = 'Update the published project';
const PROJECT_CARD_PRIMARY_ACTION_SIZE_CLASS_NAMES = 'text-xs px-3 py-1.5';
const PROJECT_CARD_PRIMARY_ACTION_CLASS_NAMES = [
  MAIN_ACTION_BUTTON_CLASS_NAMES,
  PROJECT_CARD_PRIMARY_ACTION_SIZE_CLASS_NAMES,
].join(' ');
const PROJECT_STATUS_CONTEXT_ANALYSIS_LABEL = 'Context Analysis';
const PROJECT_STATUS_ADR_LABEL = 'ADR';
const PROJECT_STATUS_DSI_GENERATION_LABEL = 'DSI Generation';
const PROJECT_STATUS_BIZZDESIGN_INTEGRATION_LABEL =
  'Bizzdesign Integration';
const PROJECT_STATUS_COMPLETED_LABEL = 'Completed';
const PROJECT_STATUS_PUBLISHED_LABEL = 'Published';
const REVIEW_STATUS_APPROVED = 'approved';

/**
 * Maps a project status to the current stage number shown in the workflow pages.
 * GENERATION maps to stage 6 (Architecture Generation), as shown in Generation.tsx.
 * Brownfield projects have an extra stage 2.5 (SAHLD Conversion).
 */
const getStageForStatus = (
  status: ProjectStatus,
  _isBrownfield: boolean,
): number => {
  switch (status) {
    case ProjectStatus.CONTEXT_UPLOAD:
      return 2;
    case ProjectStatus.CONTEXT_REVIEW:
      return 3;
    case ProjectStatus.ADR:
      return 4;
    case ProjectStatus.GENERATION:
      return GENERATION_STAGE_NUMBER;
    case ProjectStatus.BIZZDESIGN_INTEGRATION:
      return 6;
    case ProjectStatus.IN_REVIEW:
    case ProjectStatus.COMPLETED:
    case ProjectStatus.PUBLISHED:
      return FINAL_STAGE_NUMBER;
    default:
      return 1;
  }
};

interface ProjectCardProps {
  project: ProjectItem;
  onContinue: (projectId: number) => void;
  onDelete: (projectId: number) => void;
  onOpenDocument: (projectId: number) => void;
  /** When provided, a "download as Word" button is shown next to Open. */
  onDownloadDocument?: (projectId: number) => void | Promise<void>;
  onPublish?: (projectId: number) => void | Promise<void>;
  onUpdate?: (projectId: number) => void | Promise<void>;
  isInSharedTab?: boolean;
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const getPublishSummaryText = (project: ProjectItem): string | null => {
  const approverNames = project.review_summary.approver_names ?? [];
  const approvedByText =
    approverNames.length > 0
      ? `Approved by ${approverNames.join(', ')}`
      : 'Approved';
  const approvedSummaryText = project.review_summary.approved_at
    ? `${approvedByText} on ${formatDate(project.review_summary.approved_at)}`
    : approvedByText;

  if (project.publish_summary.is_published) {
    const publishedBy = project.publish_summary.published_by
      ? ` by ${project.publish_summary.published_by}`
      : '';
    const publishedAt = project.publish_summary.published_at
      ? ` on ${formatDate(project.publish_summary.published_at)}`
      : '';
    return `Published${publishedBy}${publishedAt}`;
  }
  return project.publish_summary.can_publish ? approvedSummaryText : null;
};

// The displayed status badge reflects the review/publish lifecycle: an
// approved (versioned) project shows "Completed" and a published project
// shows "Published", even though the underlying workflow status only
// advances to "published" on publish.
const getEffectiveStatus = (project: ProjectItem): ProjectStatus | string => {
  if (project.publish_summary.is_published) {
    return ProjectStatus.PUBLISHED;
  }
  if (project.version || project.status === ProjectStatus.IN_REVIEW) {
    return ProjectStatus.COMPLETED;
  }
  return project.status;
};

const VersionBadge: React.FC<{ version: ProjectItem['version'] }> = ({ version }) =>
  version ? (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-800"
      title={`Approved by all approvers and marked as ${version}`}
    >
      <CheckBadgeIcon className="w-3.5 h-3.5" />
      {version}
    </span>
  ) : (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-400"
      title={NOT_YET_APPROVED_TITLE}
    >
      <CheckBadgeIcon className="w-3.5 h-3.5" />
      {NOT_APPROVED_BADGE_TEXT}
    </span>
  );

// A project can be opened in the review screen once it has been submitted
// for review (a review record exists). Mirrors the "Go to Review" action on
// the final review page; disabled until the project is under review.
const ReviewActionButton: React.FC<{ project: ProjectItem }> = ({ project }) => {
  const navigate = useNavigate();
  const reviewId = project.review_summary.review_id;
  const hasActiveReview = Boolean(reviewId);
  return (
    <button
      className={
        hasActiveReview
          ? 'inline-flex items-center gap-1 rounded border border-indigo-300 bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-700 transition-colors hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'
          : 'inline-flex items-center gap-1 rounded border border-gray-200 bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-400 cursor-not-allowed'
      }
      onClick={() =>
        reviewId &&
        navigate(`/reviews/${reviewId}?projectId=${project.id}`)
      }
      disabled={!hasActiveReview}
      title={hasActiveReview ? REVIEW_BUTTON_TITLE : REVIEW_DISABLED_TITLE}
    >
      <EyeIcon className="h-4 w-4" />
      Review
    </button>
  );
};

const PublishActionButton: React.FC<{
  project: ProjectItem;
  onPublish: (projectId: number) => void | Promise<void>;
}> = ({ project, onPublish }) => (
  <button
    className={
      project.publish_summary.can_publish
        ? 'inline-flex items-center gap-1 rounded border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 transition-colors hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2'
        : 'inline-flex items-center gap-1 rounded border border-gray-200 bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-400 cursor-not-allowed'
    }
    onClick={() => onPublish(project.id)}
    disabled={!project.publish_summary.can_publish}
    title={
      project.publish_summary.can_publish
        ? PUBLISH_BUTTON_TITLE
        : PUBLISH_DISABLED_TITLE
    }
  >
    <ArrowUpTrayIcon className="h-4 w-4" />
    Publish
  </button>
);

/**
 * ProjectCard component for displaying individual project information in a card layout
 * Displays project title, description, progress indicators, and quick actions
 */
export const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  onContinue,
  onDelete,
  onOpenDocument,
  onDownloadDocument,
  onPublish,
  onUpdate,
  isInSharedTab = false,
}) => {
  const { user } = useAuth();
  
  const isSharedProject = user && project.owner && project.owner !== user.username;
  // Show shared info if in shared tab or if user is not the owner
  const showSharedBy = isInSharedTab || isSharedProject;
  // Use display name if available, otherwise fall back to username
  const ownerDisplayName = project.owner_display_name ?? project.owner;
  const coOwnerList = (project.co_owners ?? '')
    .split(',')
    .map((name) => name.trim())
    .filter(Boolean);
  const coOwnersCount = coOwnerList.length;
  // Comma-separated co-owner names for the "Co-authors" badge.
  const coOwnerNames = coOwnerList.join(', ');
  // Total team size: owner + co-owners
  const totalCollaborators = 1 + coOwnersCount;
  const showTeamBadge = totalCollaborators > 1;

  // Published projects are visible to every Archie user, but only the owner
  // or a co-owner may update a published project — everyone else can only
  // open it. Match the current user (by username or email) against the
  // project owner and the comma-separated co-owner usernames.
  const currentIdentities = [user?.username, user?.email]
    .filter(Boolean)
    .map((value) => (value as string).trim().toLowerCase());
  const isOwner = Boolean(
    project.owner &&
      currentIdentities.includes(project.owner.trim().toLowerCase()),
  );
  const coOwnerIdentities = (project.co_owners ?? '')
    .split(',')
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);
  const isCoOwner = currentIdentities.some((identity) =>
    coOwnerIdentities.includes(identity),
  );
  const canUpdatePublished = isOwner || isCoOwner;
  // Sharing is a project-team action: owners and co-owners can open the
  // invite dialog, mirroring the Share tab inside the project review screen.
  const canShare = isOwner || isCoOwner;
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isDownloadingDocument, setIsDownloadingDocument] = useState(false);
  // The document export needs at least one generated section; without any the
  // backend returns 404, so the download button stays disabled until there is
  // something to export. A project that is generated, under review, or already
  // published always has a document; a partially-generated one exports what
  // exists so far.
  const canDownloadDocument =
    Boolean(project.progress?.architectureGenerated) ||
    (project.progress?.sectionsCompleted ?? 0) > 0 ||
    Boolean(project.review_summary?.review_id) ||
    Boolean(project.publish_summary?.is_published);

  const handleDownloadClick = async (): Promise<void> => {
    if (!onDownloadDocument || isDownloadingDocument) {
      return;
    }
    setIsDownloadingDocument(true);
    try {
      await onDownloadDocument(project.id);
    } finally {
      setIsDownloadingDocument(false);
    }
  };
  
  const getStatusBadge = (status: ProjectStatus | string) => {
    // Convert string status to enum if needed
    let statusKey: ProjectStatus;
    if (typeof status === 'string') {
      // Map API string values to enum
      const statusMap: Record<string, ProjectStatus> = {
        'context_upload': ProjectStatus.CONTEXT_UPLOAD,
        'context_review': ProjectStatus.CONTEXT_REVIEW,
        'adr': ProjectStatus.ADR,
        'generation': ProjectStatus.GENERATION,
        'bizzdesign_integration': ProjectStatus.BIZZDESIGN_INTEGRATION,
        'in_review': ProjectStatus.IN_REVIEW,
        'completed': ProjectStatus.COMPLETED,
        'published': ProjectStatus.PUBLISHED,
      };
      statusKey = statusMap[status] ?? ProjectStatus.CONTEXT_UPLOAD;
    } else {
      statusKey = status;
    }
    const statusConfig = {
      [ProjectStatus.CONTEXT_UPLOAD]: { 
        color: 'bg-yellow-100 text-yellow-800', 
        icon: ClockIcon, 
        text: CONTEXT_MANAGEMENT_LABEL,
      },
      [ProjectStatus.CONTEXT_REVIEW]: { 
        color: 'bg-blue-100 text-blue-800', 
        icon: ClockIcon, 
        text: PROJECT_STATUS_CONTEXT_ANALYSIS_LABEL,
      },
      [ProjectStatus.ADR]: {
        color: 'bg-red-100 text-red-800',
        icon: DocumentTextIcon,
        text: PROJECT_STATUS_ADR_LABEL,
      },
      [ProjectStatus.GENERATION]: { 
        color: 'bg-purple-100 text-purple-800', 
        icon: ClockIcon, 
        text: PROJECT_STATUS_DSI_GENERATION_LABEL,
      },
      [ProjectStatus.BIZZDESIGN_INTEGRATION]: {
        color: 'bg-cyan-100 text-cyan-800',
        icon: WrenchScrewdriverIcon,
        text: PROJECT_STATUS_BIZZDESIGN_INTEGRATION_LABEL,
      },
      [ProjectStatus.IN_REVIEW]: {
        color: 'bg-indigo-100 text-indigo-800',
        icon: DocumentTextIcon,
        text: 'In Review',
      },
      [ProjectStatus.COMPLETED]: { 
        color: 'bg-green-100 text-green-800', 
        icon: CheckCircleIcon, 
        text: PROJECT_STATUS_COMPLETED_LABEL,
      },
      [ProjectStatus.PUBLISHED]: {
        color: 'bg-emerald-100 text-emerald-800',
        icon: CheckCircleIcon,
        text: PROJECT_STATUS_PUBLISHED_LABEL,
      },
    };

    const config = statusConfig[statusKey];
    const IconComponent = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color} gap-1`}>
        <IconComponent className="w-3 h-3" />
        {config.text}
      </span>
    );
  };

  const getReviewStatusBadge = (status: ProjectItem['review_summary']['status']) => {
    const statusConfig = {
      not_submitted: 'bg-gray-100 text-gray-700',
      in_review: 'bg-indigo-100 text-indigo-800',
      approved: 'bg-green-100 text-green-800',
      changes_requested: 'bg-amber-100 text-amber-800',
      closed: 'bg-slate-100 text-slate-700',
    };

    const labelMap = {
      not_submitted: 'Not Submitted',
      in_review: 'In Review',
      approved: 'Approved',
      changes_requested: 'Changes Requested',
      closed: 'Closed',
    };

    return (
      <span
        className={[
          'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
          statusConfig[status],
        ].join(' ')}
      >
        Review: {labelMap[status]}
      </span>
    );
  };

  const getProjectTypeIcon = () => {
    if (!project.projectType) {return null;}

    const projectTypeConfig = {
      'green_field': {
        icon: SparklesIcon,
        color: 'text-accent-primary-strong',  // Shell Yellow (darker for visibility)
        bgColor: 'bg-accent-primary-subtle',
        title: 'Green Field Project - New development from scratch',
      },
      'brown_field': {
        icon: WrenchScrewdriverIcon,
        color: 'text-accent-secondary-strong',  // Shell Red
        bgColor: 'bg-accent-secondary-subtle',
        title: 'Brown Field Project - Enhancement of existing system',
      },
    };

    const config = projectTypeConfig[project.projectType as keyof typeof projectTypeConfig];
    if (!config) {return null;}

    const IconComponent = config.icon;

    return (
      <div 
        className={`inline-flex items-center p-1.5 rounded-full ${config.bgColor} ${config.color}`}
        title={config.title}
      >
        <IconComponent className="w-4 h-4" />
      </div>
    );
  };

  const getWorkflowProgressPercentage = (): number => {
    const isBrownfield = project.project_type === ProjectType.BROWN_FIELD;
    const stages = isBrownfield ? BROWNFIELD_STAGES : GREENFIELD_STAGES;
    const divisor = stages.length - 1;

    const STATUS_MAP: Record<string, ProjectStatus> = {
      context_upload: ProjectStatus.CONTEXT_UPLOAD,
      context_review: ProjectStatus.CONTEXT_REVIEW,
      adr: ProjectStatus.ADR,
      generation: ProjectStatus.GENERATION,
      bizzdesign_integration: ProjectStatus.BIZZDESIGN_INTEGRATION,
      in_review: ProjectStatus.IN_REVIEW,
      completed: ProjectStatus.COMPLETED,
      published: ProjectStatus.PUBLISHED,
    };

    const statusEnum = typeof project.status === 'string'
      ? (STATUS_MAP[project.status] ?? ProjectStatus.CONTEXT_UPLOAD)
      : project.status;

    if (project.publish_summary.is_published) {
      return 100;
    }

    if (project.review_summary.status === REVIEW_STATUS_APPROVED) {
      return 100;
    }

    const totalSections = Math.max(project.progress.totalSections, 0);
    const sectionsCompleted = Math.min(
      project.progress.sectionsCompleted,
      totalSections,
    );
    const completedMilestones = [
      project.progress.contextUploaded,
      project.progress.contextReviewed,
      project.progress.architectureGenerated,
    ].filter(Boolean).length;

    if (totalSections > 0) {
      const completedSteps = completedMilestones + sectionsCompleted;
      const totalSteps = totalSections + 3;
      const progressPercentage = Math.round(
        (completedSteps / totalSteps) * 100,
      );
      return Math.min(progressPercentage, 99);
    }

    const currentStageId = getStageForStatus(statusEnum, isBrownfield);
    const completedCount = stages.filter(s => s < currentStageId).length;
    const totalProgress = completedCount + 0.5;

    return Math.min(Math.round((totalProgress / divisor) * 100), 100);
  };

  const publishSummaryText = getPublishSummaryText(project);
  const effectiveStatus = getEffectiveStatus(project);

  return (
    <div className="bg-white shadow-lg hover:shadow-xl transition-all duration-200 border border-gray-200 rounded-lg h-full flex flex-col">
      <div className="p-6 flex flex-col flex-grow">
        {/* Header with title and status */}
        <div className="flex justify-between items-start mb-2">
          <div className="flex items-start gap-2 flex-1 pr-3 min-w-0">
            {getProjectTypeIcon()}
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
                {project.title}
              </h3>
            </div>
          </div>
          <div className="flex flex-shrink-0 flex-col items-end gap-1.5">
            <div className="flex items-center gap-1.5">
              {getStatusBadge(effectiveStatus)}
              <VersionBadge version={project.version} />
              {showTeamBadge && (
                <div
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-accent-tertiary-subtle text-accent-tertiary-strong gap-1 cursor-default"
                  title={`${totalCollaborators} team member${totalCollaborators > 1 ? 's' : ''}`}
                >
                  <UserGroupIcon className="w-3 h-3" />
                  {totalCollaborators}
                </div>
              )}
            </div>
            <div className="flex max-w-xs flex-wrap items-center justify-end gap-1.5">
              {getReviewStatusBadge(project.review_summary.status)}
              {publishSummaryText && (
                <span className="text-right text-xs font-medium text-gray-500">
                  {publishSummaryText}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Shared By Badge - Full Width Row */}
        {showSharedBy && (
          <div className="flex items-center gap-1 mb-3">
            <UserGroupIcon className="w-3.5 h-3.5 text-accent-tertiary-strong flex-shrink-0" />
            <span className="text-xs text-accent-tertiary-strong font-medium">
              Shared by {ownerDisplayName}
            </span>
          </div>
        )}

        {/* Description */}
        <div className="flex-grow">
          {project.description && (
            <p className="text-gray-600 text-sm line-clamp-3 mb-4">
              {project.description}
            </p>
          )}

          {/* Author / Co-author access summary - shows who created the
              project and, when shared, the co-owners who also have access. */}
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <span
              className="inline-flex max-w-full items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700"
              title={`Created by ${ownerDisplayName}`}
            >
              Author: {ownerDisplayName}
            </span>
            {coOwnersCount > 0 && (
              <span
                className="inline-flex max-w-full items-center rounded-full bg-accent-tertiary-subtle px-2 py-0.5 text-xs font-medium text-accent-tertiary-strong"
                title={`Shared with ${coOwnerNames}`}
              >
                Co-author{coOwnersCount > 1 ? 's' : ''}: {coOwnerNames}
              </span>
            )}
          </div>
        </div>

        {/* Progress Indicators */}
        <div className="space-y-2 mb-4">
          <div className="flex justify-between items-center text-xs">
            <span className="text-gray-500">Workflow Progress</span>
            <span className="text-gray-500">{getWorkflowProgressPercentage()}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
              style={{ width: `${getWorkflowProgressPercentage()}%` }}
            />
          </div>
        </div>

        {/* Meta Information */}
        <div className="flex justify-between items-center text-xs text-gray-400 mb-4">
          <span>Created: {formatDate(project.created_at)}</span>
          <span>Updated: {formatDate(project.updated_at)}</span>
        </div>

        {/* Actions — primary action on its own row, secondary actions below,
            so buttons stay aligned and never orphan when the card is narrow
            (e.g. the chat panel is open or the window is resized). */}
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            {/* Published projects are complete; they offer Update (owners) and
                Open (everyone) instead of Continue. */}
            {!project.publish_summary.is_published && (
              <ContinueButton
                projectId={project.id}
                projectStatus={project.status}
                onClick={() => onContinue(project.id)}
                className={PROJECT_CARD_PRIMARY_ACTION_SIZE_CLASS_NAMES}
              />
            )}
            {project.publish_summary.is_published && canUpdatePublished && (
              <button
                className={PROJECT_CARD_PRIMARY_ACTION_CLASS_NAMES}
                onClick={() => onUpdate?.(project.id)}
                title={UPDATE_BUTTON_TITLE}
              >
                <DocumentTextIcon className="w-4 h-4" />
                Update
              </button>
            )}
          </div>
          
          <div className="flex flex-wrap items-center gap-2">
            {canShare && (
              <button
                className="inline-flex items-center gap-1 rounded border border-blue-300 bg-blue-50 px-3 py-1.5 text-xs font-medium text-[#336094] transition-colors hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-[#336094] focus:ring-offset-2"
                onClick={() => setIsShareModalOpen(true)}
                title="Share project / invite collaborators"
              >
                <ShareIcon className="h-4 w-4" />
                Share
              </button>
            )}
            <ReviewActionButton project={project} />
            {!project.publish_summary.is_published && onPublish && (
              <PublishActionButton project={project} onPublish={onPublish} />
            )}
            {/* Icon utilities are bonded so open-document and delete wrap
                together as a unit rather than the delete icon orphaning onto
                its own line. */}
            <div className="flex items-center gap-2">
              {onDownloadDocument && (
                <button
                  className="inline-flex items-center p-1.5 border border-gray-300 rounded text-[#336094] bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#336094] transition-colors disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white"
                  onClick={handleDownloadClick}
                  disabled={!canDownloadDocument || isDownloadingDocument}
                  title={
                    canDownloadDocument
                      ? 'Download latest document as Word'
                      : 'Available once the architecture has been generated'
                  }
                >
                  {isDownloadingDocument ? (
                    <span className="loading loading-spinner loading-xs" />
                  ) : (
                    <DocumentArrowDownIcon className="w-4 h-4" />
                  )}
                </button>
              )}
              <button
                className="inline-flex items-center p-1.5 border border-gray-300 rounded text-[#336094] bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#336094] transition-colors"
                onClick={() => onOpenDocument(project.id)}
                title="Open document"
              >
                <DocumentTextIcon className="w-4 h-4" />
              </button>

              {/* A published project may only be deleted by its owner or a
                  co-owner; non-owners viewing it on the Published tab cannot
                  delete it. Non-published projects keep their existing delete. */}
              {(!project.publish_summary.is_published || canUpdatePublished) && (
                <button
                  className="inline-flex items-center p-1.5 border border-gray-300 rounded text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                  onClick={() => onDelete(project.id)}
                  title="Delete project"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {canShare && (
        <InviteCollaboratorsModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          projectId={project.id}
          projectTitle={project.title}
        />
      )}
    </div>
  );
};
